// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Fiscal/Impostos.tsx
//  Totais de impostos apurados das NF-es autorizadas no período.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { DollarSign, FileText, TrendingUp, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { useImpostosResumo } from '@/features/fiscal/hooks/useNotaFiscal';

// ── helpers ──────────────────────────────────────────────────────────────────

const fmtCur = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const today = () => new Date().toISOString().slice(0, 10);

// ── KPI Card ──────────────────────────────────────────────────────────────────

function KpiCard({
  title,
  value,
  sub,
  icon: Icon,
  loading,
}: {
  title:   string;
  value:   string;
  sub:     string;
  icon:    React.ElementType;
  loading: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Icon className="h-4 w-4" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <>
            <Skeleton className="h-7 w-32 mb-1" />
            <Skeleton className="h-3 w-24" />
          </>
        ) : (
          <>
            <div className="text-2xl font-bold text-foreground">{value}</div>
            <p className="text-xs text-muted-foreground mt-1">{sub}</p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Página
// ══════════════════════════════════════════════════════════════════════════════

const Impostos = () => {
  const [dtInicio, setDtInicio] = useState(daysAgo(30));
  const [dtFim,    setDtFim]    = useState(today());

  const { data, isLoading } = useImpostosResumo(dtInicio, dtFim);

  // Impostos com valor > 0 para a barra de composição
  const barras = [
    { nome: 'ICMS',        valor: data?.totalIcms    ?? 0, cor: 'bg-blue-500'   },
    { nome: 'ICMS-ST',     valor: data?.totalIcmsSt  ?? 0, cor: 'bg-blue-300'   },
    { nome: 'PIS',         valor: data?.totalPis     ?? 0, cor: 'bg-green-500'  },
    { nome: 'COFINS',      valor: data?.totalCofins  ?? 0, cor: 'bg-purple-500' },
    { nome: 'IPI',         valor: data?.totalIpi     ?? 0, cor: 'bg-orange-500' },
    { nome: 'DIFAL',       valor: data?.totalDifal   ?? 0, cor: 'bg-yellow-500' },
    { nome: 'FCP',         valor: data?.totalFcp     ?? 0, cor: 'bg-red-400'    },
    { nome: 'IBS',         valor: data?.totalIbs     ?? 0, cor: 'bg-teal-500'   },
    { nome: 'CBS',         valor: data?.totalCbs     ?? 0, cor: 'bg-cyan-500'   },
  ].filter((b) => b.valor > 0);

  const totalImpostos = barras.reduce((s, b) => s + b.valor, 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Impostos</h1>
          <p className="text-muted-foreground mt-1">
            Totais apurados das NF-es autorizadas no período
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">De</span>
            <Input
              type="date"
              value={dtInicio}
              onChange={(e) => setDtInicio(e.target.value)}
              className="w-40"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Até</span>
            <Input
              type="date"
              value={dtFim}
              onChange={(e) => setDtFim(e.target.value)}
              className="w-40"
            />
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard
          title="Total de Impostos"
          value={fmtCur(totalImpostos)}
          sub={`${data?.totalNotas ?? 0} NF-es autorizadas`}
          icon={DollarSign}
          loading={isLoading}
        />
        <KpiCard
          title="Faturamento Bruto"
          value={fmtCur(data?.totalBruto ?? 0)}
          sub="Soma dos itens das NF-es"
          icon={TrendingUp}
          loading={isLoading}
        />
        <KpiCard
          title="Carga Tributária"
          value={data && data.totalBruto > 0
            ? `${((totalImpostos / data.totalBruto) * 100).toFixed(2)}%`
            : '—'}
          sub="Impostos / Faturamento bruto"
          icon={BarChart3}
          loading={isLoading}
        />
      </div>

      {/* Composição */}
      <Card>
        <CardHeader>
          <CardTitle>Composição de Impostos</CardTitle>
          <CardDescription>
            Total:{' '}
            {isLoading
              ? '...'
              : fmtCur(totalImpostos)}{' '}
            — período {new Date(dtInicio + 'T12:00').toLocaleDateString('pt-BR')} a{' '}
            {new Date(dtFim + 'T12:00').toLocaleDateString('pt-BR')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-2 w-full" />
              </div>
            ))
          ) : barras.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              Nenhum imposto apurado no período selecionado.
            </p>
          ) : (
            barras.map((b) => (
              <div key={b.nome} className="space-y-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${b.cor}`} />
                    <span className="font-medium text-sm text-foreground">{b.nome}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">
                      {totalImpostos > 0 ? `${((b.valor / totalImpostos) * 100).toFixed(1)}%` : '—'}
                    </span>
                    <span className="text-sm font-mono text-muted-foreground">{fmtCur(b.valor)}</span>
                  </div>
                </div>
                <Progress
                  value={totalImpostos > 0 ? (b.valor / totalImpostos) * 100 : 0}
                  className="h-2"
                />
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Detalhamento */}
      {!isLoading && data && (data.totalIcms > 0 || data.totalPis > 0 || data.totalCofins > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              Detalhamento por Tributo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { label: 'ICMS',    value: data.totalIcms    },
                { label: 'ICMS-ST', value: data.totalIcmsSt  },
                { label: 'IPI',     value: data.totalIpi     },
                { label: 'PIS',     value: data.totalPis     },
                { label: 'COFINS',  value: data.totalCofins  },
                { label: 'DIFAL',   value: data.totalDifal   },
                { label: 'FCP',     value: data.totalFcp     },
                { label: 'IBS',     value: data.totalIbs     },
                { label: 'CBS',     value: data.totalCbs     },
              ].map(({ label, value }) => (
                <div key={label} className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-base font-semibold tabular-nums">{fmtCur(value)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Impostos;
